// Lightweight i18n for the admin SPA. Two locales (zh, en) — every UI string
// goes through `t('key')`. Add a new key by editing the dictionary below; the
// type system will warn unused/typo'd keys at the call site.
//
// Persistence: `fuelio.admin.lang` in localStorage; falls back to navigator.language.

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

export type Lang = 'zh' | 'en'

const KEY = 'fuelio.admin.lang'

// ─────────────── Dictionary ───────────────
//
// Keys are namespaced by screen / domain. Values are
// (string) | ((vars) => string) so we can interpolate.

type Phrase = string | ((vars: Record<string, string | number>) => string)

const dict: Record<Lang, Record<string, Phrase>> = {
  zh: {
    // App-wide
    'app.brand.role':           '管理员',
    'app.action.signOut':       '退出登录',
    'app.action.toggleTheme':   '切换主题',
    'app.action.openMenu':      '打开菜单',
    'app.action.back':          '返回',
    'app.action.toggleLang':    'EN',           // shows the *other* lang as the toggle target
    'app.theme.light':          '浅色',
    'app.theme.dark':           '深色',
    'app.loading':              '加载中…',
    'app.signOut.confirm.title':  '确认退出登录？',
    'app.signOut.confirm.body':   '退出后你将返回登录页，需要重新登录才能继续访问后台。',
    'app.signOut.confirm.confirm':'退出',
    'app.signOut.confirm.cancel': '取消',
    'app.signOut.toast.title':   '已成功退出登录',
    'app.login.title':       '登录 Fuelio Admin',
    'app.login.subtitle':    '管理后台 · 仅限授权管理员访问',
    'app.login.signedOut':   '已退出登录，请重新登录以继续访问后台。',
    'app.login.cta':         '登录',
    'app.login.via':         '通过 Cloudflare Access 验证身份',

    // Layout error state
    'layout.error.title':        '管理员后台不可用',
    'layout.error.unauthorized': '未授权 —— Cloudflare Access 没有放行你。',
    'layout.error.misconfig':    '管理员未配置：开发环境请设 DEV_ADMIN_BYPASS=1，生产环境请设 CF_ACCESS_*。',
    'layout.error.network':      '无法连接到管理员 API。',

    // Sidebar nav
    'nav.stats':    '总览',
    'nav.users':    '用户',
    'nav.products': '商品',
    'nav.policies': '策略模板',
    'nav.orders':   '订单',
    'nav.dict':     '数据字典',
    'nav.menus':    '菜单管理',
    'nav.announcements': '公告 / 新闻',
    'nav.platformConfig': '平台配置',
    'nav.chat':     '在线客服',
    'nav.aiChat':   'AI 客服',
    'nav.group.users':    '用户管理',
    'nav.group.products': '商品管理',
    'nav.group.platform': '平台管理',
    'nav.group.support':  '客户服务',
    'nav.group.dataSync': '数据同步',
    'nav.dataSync':       '数据源',

    // Chat (admin)
    'chat.title':           '在线客服',
    'chat.empty':           '暂无对话',
    'chat.unread':          '未读',
    'chat.statusOnline':    '在线',
    'chat.statusBusy':      '忙碌',
    'chat.statusOffline':   '离线',
    'chat.placeholder':     '输入消息回复（Enter 发送）…',
    'chat.send':            '发送',
    'chat.image':           '图片',
    'chat.disconnected':    '连接断开，重新连接中…',
    'chat.selectConv':      '从左侧选择一个会话开始',
    'chat.notify':          '声音提醒',

    // Stats
    'stats.title':            '总览',
    'stats.subtitle':         '账户、商品、订单的快照。图表展示最近 14 天。',
    'stats.kpi.users':        '用户总数',
    'stats.kpi.activeProducts': '在售商品',
    'stats.kpi.orders14':     '14 天订单',
    'stats.kpi.revenue14':    '14 天已支付',
    'stats.orders.title':     '订单状态（累计）',
    'stats.orders.totalGmv':  ({ total, gmv }) => `${total} 笔 · 跨所有状态 GMV $${gmv}`,
    'stats.recent.title':     '最近活动',
    'stats.recent.empty':     '暂无活动记录',
    'stats.event.orderPlaced': ({ id, amount }) => `下单 · ${id} · $${amount}`,
    'stats.event.orderUpdate': ({ id, to }) => `订单 ${id} → ${to ?? '?'}`,
    'stats.event.productCreate': ({ sku }) => `新建商品 · ${sku ?? '?'}`,
    'stats.event.productUpdate': ({ id }) => `更新商品 · ${id ?? '?'}`,
    'stats.event.productDeactivate': ({ id }) => `停用商品 · ${id ?? '?'}`,
    'stats.event.userUpdate': ({ id }) => `更新用户 · ${id ?? '?'}`,
    'stats.event.ordersExport': ({ rows }) => `导出订单 CSV（${rows} 行）`,
    'stats.event.usersExport': ({ rows }) => `导出用户 CSV（${rows} 行）`,
    'stats.event.referralAward': ({ amount, id }) => `推荐返佣 · $${amount} → ${id}`,

    // Status pills
    'status.pending':   '待支付',
    'status.paid':      '已支付',
    'status.fulfilled': '已发货',
    'status.refunded':  '已退款',
    'status.cancelled': '已取消',
    'status.active':    '在售',
    'user.status.active':   '正常',
    'user.status.disabled': '已停用',
    'user.status.pending':  '待激活',
    'status.inactive':  '已下架',
    'status.disabled':  '已禁用',

    // Common actions
    'action.search':      '搜索',
    'action.cancel':      '取消',
    'action.save':        '保存',
    'action.edit':        '编辑',
    'action.disable':     '停用',
    'action.enable':      '启用',
    'action.delete':      '删除',
    'action.refund':      '退款',
    'action.export':      '导出 CSV',
    'action.exporting':   '导出中…',

    // Products
    'products.title':           '商品',
    'products.subtitle':        ({ n }) => `共 ${n} 条 · 含已下架`,
    'products.action.new':      '新建商品',
    'products.col.sku':         'SKU',
    'products.col.name':        '名称',
    'products.col.type':        '类型',
    'products.col.price':       '价格',
    'products.col.active':      '状态',
    'products.col.actions':     '操作',
    'products.editor.titleNew': '新建商品',
    'products.editor.titleEdit':'编辑商品',
    'products.field.sku':       'SKU（自动大写）',
    'products.field.type':      '订单类型',
    'products.field.currency':  '币种',
    'products.field.price':     '价格（分）',
    'products.field.priceHint': ({ amt }) => `约 ${amt}`,
    'products.field.nameEn':    '英文名',
    'products.field.nameZh':    '中文名',
    'products.field.active':    '激活（在 /shop/products 中显示）',
    'products.field.image':     '图片',
    'products.field.imageHint': 'PNG / JPG / WebP / SVG · ≤ 4 MB',
    'products.field.upload':    '上传',
    'products.field.replace':   '替换',
    'products.field.uploading': '上传中…',
    'products.field.remove':    '移除',
    'products.field.category':  '商品分类',
    'products.field.featured':  '推荐（在"推荐"标签置顶）',
    'products.field.coverTag':            '封面标签（购物卡右上角 chip）',
    'products.field.coverTagPlaceholder': '如：热销 / 少量到货 / 限时',
    'products.field.coverTagHint':        '6 字以内最佳；颜色留空 ✕ = 不显示',
    'products.toast.created':   '已创建',
    'products.toast.updated':   '已更新',
    'products.toast.disabled':  '已停用',
    'products.toast.enabled':   '已启用',
    'products.toast.deleted':   '已删除',
    'products.toast.deleteFailed':'删除失败',
    'products.toast.saveFailed':'保存失败',
    'products.toast.updateFailed':'更新失败',
    'products.field.priceUsd':  'USD 价格 ($)',
    'products.field.priceCny':  'CNY 价格 (¥)',
    'products.field.priceUsdOrig': 'USD 原价（划线）',
    'products.field.priceCnyOrig': 'CNY 原价（划线）',
    'products.field.priceHelp': '至少填一个现价；原价填了就显示「划线 + 省 ¥N」。',
    'products.field.subtitleZh': '副标题（中文）',
    'products.field.subtitleEn': '副标题（英文）',
    'products.field.topupColMonths': '月数',
    'products.field.topupColCnyOrig': 'CNY 原价',
    'products.field.topupColUsdOrig': 'USD 原价',
    'products.field.detailSection': '商品详情页字段',
    'products.field.detailHint':    '（可选；用于商城商品详情页）',
    'products.field.images':        '主图',
    'products.field.imagesHint':    '最多 5 张；第一张默认为封面（可点星标切换）',
    'products.field.cover':         '封面',
    'products.field.makeCover':     '设为封面',
    'products.field.brand':         '品牌',
    'products.field.variant':       '分类',
    'products.field.variantTopup':  '代充',
    'products.field.variantReady':  '成品号',
    'products.field.topupOptions':  '代充时长选项（每档独立定价）',
    'products.field.topupOptionsHint': '月数 / CNY 价格 / USD 价格；为空则按基础价。下方可一键添加常用档位。',
    'products.field.topupMonthsSuffix': '个月',
    'products.field.emailRequired': '邮箱',
    'products.field.emailNever':    '不需要',
    'products.field.emailCart':     '加购必填',
    'products.field.emailBuy':      '购买必填',
    'products.field.tags':          '标签',
    'products.field.addTag':        '添加标签',
    'products.field.descMdZh':      '宝贝详情（中文，Markdown，可插图）',
    'products.field.descMdEn':      '宝贝详情（英文，Markdown）',
    'products.field.markdownInsertImage': '插入图片',
    'products.field.policyRefs':    '复用模板（在「策略模板」页维护）',
    'products.field.policyNone':    '不使用',
    'products.field.soldCount':     '展示销量',
    'products.field.stock':         '库存',
    'products.field.autoFulfill':   '自动发货',
    'products.field.autoFulfillHint': '付款后系统自动发货，无需人工操作',
    'products.field.autoFulfillNote': '自动发货内容（凭证/说明）',
    'products.field.autoFulfillNotePlaceholder': '例：账号：xxx@gmail.com\n密码：xxxxxxxx\n使用说明：…',
    // ─── Policy templates ───
    'policies.title':         '策略模板',
    'policies.subtitle':      ({ n }) => `共 ${n} 个`,
    'policies.action.new':    '新建模板',
    'policies.filter.all':    '全部类型',
    'policies.type.delivery': '发货格式',
    'policies.type.address':  '使用地址',
    'policies.type.notice':   '注意事项',
    'policies.col.type':      '类型',
    'policies.col.slug':      '标识 (slug)',
    'policies.col.title':     '名称',
    'policies.col.actions':   '操作',
    'policies.editor.titleNew':  '新建策略模板',
    'policies.editor.titleEdit': '编辑策略模板',
    'policies.field.type':       '类型（创建后不可修改）',
    'policies.field.slug':       '标识 / slug',
    'policies.field.titleZh':    '名称（中文）',
    'policies.field.titleEn':    '名称（英文）',
    'policies.delivery.key':    '字段名',
    'policies.delivery.value':  '内容',
    'policies.delivery.addRow': '添加一行',
    'policies.delivery.hint':   '底部说明（可选）',
    'policies.address.label':   '标签',
    'policies.address.value':   '值',
    'policies.address.sub':     '副文（可选）',
    'policies.address.addCell': '添加格',
    'policies.address.ipNote':  'IP / 网络说明（可选）',
    'policies.address.markdown':              '富文本 Markdown（可选 · 优先于下面的格子）',
    'policies.address.markdownPlaceholder':   '留空 = 使用下面的"格子 + IP 说明"布局\n填了 = 在商品页"使用说明"标签直接渲染这段 markdown\n\n示例：\n## 📖 标题\n### 章节\n- 列表\n```code```\n> 引用框',
    'policies.address.markdownActiveHint':    'Markdown 已填，下方"格子 + IP 说明"将不会在商品页显示（仍可保留作草稿）。',
    'policies.notice.itemTitle': '条目标题',
    'policies.notice.itemBody':  '条目内容',
    'policies.notice.addItem':   '添加条目',
    'policies.toast.created':       '已创建',
    'policies.toast.updated':       '已更新',
    'policies.toast.deleted':       '已删除',
    'policies.toast.deleteFailed':  '删除失败',
    'policies.toast.saveFailed':    '保存失败',
    'policies.confirmDelete.title':   '确认删除策略模板？',
    'policies.confirmDelete.body':    ({ slug }) => `将永久删除 ${slug}。引用此模板的商品的对应栏目会显示为空。`,
    'policies.confirmDelete.confirm': '删除',
    'policies.confirmDelete.cancel':  '取消',

    // Data dictionaries (variant / cat / brand)
    'dict.title':              '数据字典',
    'dict.subtitle':           ({ n }) => `${n} 条记录`,
    'dict.tab.variant':        '订单类型',
    'dict.tab.cat':            '商品分类',
    'dict.tab.brand':          '品牌',
    'dict.action.new':         '新增',
    'dict.col.code':           'code',
    'dict.col.labelZh':        '中文名',
    'dict.col.labelEn':        '英文名',
    'dict.col.sortOrder':      '排序',
    'dict.col.active':         '启用',
    'dict.col.actions':        '操作',
    'dict.empty':              '暂无字典项',
    'dict.editor.titleNew':    '新增字典项',
    'dict.editor.titleEdit':   '编辑字典项',
    'dict.field.code':         'code（保存后慎改）',
    'dict.field.code.hint':    '小写字母、数字、连字符；写入商品 metadata 的值',
    'dict.field.labelZh':      '中文名',
    'dict.field.labelEn':      '英文名（可选）',
    'dict.field.sortOrder':    '排序（小→前）',
    'dict.field.active':       '启用（关闭后客户端不再展示）',
    'dict.action.save':        '保存',
    'dict.action.cancel':      '取消',
    'dict.toast.created':      '已新增',
    'dict.toast.updated':      '已更新',
    'dict.toast.deactivated':  '已停用',
    'dict.toast.saveFailed':   '保存失败',
    'dict.toast.deleteFailed': '停用失败',
    'dict.confirmDelete.title':   '确认停用？',
    'dict.confirmDelete.body':    ({ code }) => `将把 ${code} 标记为停用。已使用该值的旧商品仍可解析名称，新商品/前端不再展示。`,
    'dict.confirmDelete.confirm': '停用',
    'dict.confirmDelete.cancel':  '取消',

    // Menus management (presentation overlay over hardcoded routes)
    'menus.title':           '菜单管理',
    'menus.subtitle':        ({ n }) => `${n} 项菜单`,
    'menus.tab.marketing':   '前端页面',
    'menus.tab.workspace':   '工作台页面',
    'menus.tab.admin':       '管理后台页面',
    'menus.col.code':        'code',
    'menus.col.labelZh':     '中文名',
    'menus.col.labelEn':     '英文名',
    'menus.col.sortOrder':   '排序',
    'menus.col.visible':     '显示',
    'menus.col.active':      '启用',
    'menus.col.actions':     '操作',
    'menus.action.save':     '保存',
    'menus.toast.saved':     '已保存',
    'menus.toast.saveFailed':'保存失败',

    'products.confirmDelete.title':   '确认删除商品？',
    'products.confirmDelete.body':    ({ sku }) => `将永久删除 ${sku}。此操作不可恢复，过往订单条目仍会引用该商品 ID 但无法显示名称/图片。`,
    'products.confirmDelete.confirm': '删除',
    'products.confirmDelete.cancel':  '取消',

    // Users
    'users.title':         '用户',
    'users.subtitle':      ({ n }) => `共 ${n} 条`,
    'users.search':        '搜索邮箱、昵称、邀请码…',
    'users.filter.all':    '全部状态',
    'users.empty':         '没有匹配的用户',
    'users.toggle':        ({ from, to }) => `${from} → ${to}`,
    'users.col.coins':     '金币 (¥)',
    'users.col.role':      '角色',
    'users.action.adjustWallet': '调整金币',
    'users.toast.walletAdjusted':    '金币已调整',
    'users.toast.walletAdjustFailed':'调整金币失败',
    'users.walletDialog.title':   '调整用户金币',
    'users.walletDialog.current': '当前余额',
    'users.walletDialog.delta':   '增减额（正数加，负数扣）',
    'users.walletDialog.deltaHint': '单位：元（¥）。1 金币 = ¥1。',
    'users.walletDialog.note':    '备注（必填）',
    'users.walletDialog.notePh':  '邀请返利 / 客服补偿 / 退款 …',
    'users.walletDialog.after':   '调整后余额',
    'users.action.setPassword': '改密码',
    'users.toast.passwordSet':       '密码已更新',
    'users.toast.passwordSetFailed': '密码更新失败',
    'users.passwordDialog.title':   '修改用户密码',
    'users.passwordDialog.label':   '新密码',
    'users.passwordDialog.hint':    '8~128 位。设置后请尽快通知用户本人。',
    'users.passwordDialog.confirm': '确认密码',
    'users.passwordDialog.mismatch':'两次输入不一致',
    'users.passwordDialog.tooShort':'至少 8 位',

    // Orders
    'orders.title':        '订单',
    'orders.subtitle':     ({ n }) => `共 ${n} 条`,
    'orders.search':       '按订单 ID 末 8 位搜索…',
    'orders.col.order':    '订单',
    'orders.col.user':     '用户',
    'orders.col.amount':   '金额',
    'orders.col.status':   '状态',
    'orders.col.provider': '支付通道',
    'orders.col.created':  '创建时间',
    'orders.col.actions':  '操作',
    'orders.empty':        '没有匹配的订单',
    'orders.confirm.cancel':'确认取消订单 {id}？',
    'orders.confirm.refund':'确认退款订单 {id}？',
    'orders.toast.statusChanged': ({ from, to }) => `${from} → ${to}`,
    'orders.toast.exportReady':   'CSV 已下载',
    'orders.toast.exportFailed':  '导出失败',
    'orders.toast.updateFailed':  '更新失败',

    // Fulfillment dialog（"已支付 → 已发货" 这一步）
    'orders.fulfill.cta':         '发货',
    'orders.fulfill.title':       '标记订单发货',
    'orders.fulfill.body':        '账号类商品（成品号等）请把账号 / 密码 / 登录地址等凭证粘到下方文本框，用户在「我的订单」会看到这段内容并可一键复制。代充 / 服务类商品无需凭证，留空提交即可。',
    'orders.fulfill.placeholder': '账号: ...\n密码: ...\n登录地址: ...\n备注: ...\n（留空表示无需凭证 — 代充 / 服务类）',
    'orders.fulfill.hint':        '凭证仅订单买家可见。提交后无法直接修改文本，请先核对内容。',
    'orders.fulfill.submitWith':  '确认发货（含凭证）',
    'orders.fulfill.submitNone':  '确认发货（无需凭证）',
    'orders.fulfill.cancel':      '取消',
    'orders.fulfill.toastSent':   '已标记发货',
  },

  en: {
    'app.brand.role':           'Admin',
    'app.action.signOut':       'Sign out',
    'app.action.toggleTheme':   'Toggle theme',
    'app.action.openMenu':      'Open menu',
    'app.action.back':          'Back',
    'app.action.toggleLang':    '中',
    'app.theme.light':          'Light',
    'app.theme.dark':           'Dark',
    'app.loading':              'Loading…',
    'app.signOut.confirm.title':  'Sign out?',
    'app.signOut.confirm.body':   "You'll return to the login page and need to sign in again to access the admin.",
    'app.signOut.confirm.confirm':'Sign out',
    'app.signOut.confirm.cancel': 'Cancel',
    'app.signOut.toast.title':   'Signed out',
    'app.login.title':       'Sign in to Fuelio Admin',
    'app.login.subtitle':    'Authorized administrators only',
    'app.login.signedOut':   "You've been signed out. Sign in again to continue.",
    'app.login.cta':         'Sign in',
    'app.login.via':         'Authenticated via Cloudflare Access',

    'layout.error.title':        'Admin unavailable',
    'layout.error.unauthorized': 'Unauthorized — Cloudflare Access did not authenticate you.',
    'layout.error.misconfig':    'Admin not configured. Set DEV_ADMIN_BYPASS=1 (dev) or CF_ACCESS_* (prod).',
    'layout.error.network':      'Could not reach the admin API.',

    'nav.stats':    'Stats',
    'nav.users':    'Users',
    'nav.products': 'Products',
    'nav.policies': 'Policies',
    'nav.orders':   'Orders',
    'nav.dict':     'Dictionaries',
    'nav.menus':    'Menus',
    'nav.announcements': 'News & Announcements',
    'nav.platformConfig': 'Platform config',
    'nav.chat':     'Live chat',
    'nav.aiChat':   'AI assistant',
    'nav.group.users':    'User management',
    'nav.group.products': 'Product management',
    'nav.group.platform': 'Platform management',
    'nav.group.support':  'Support',
    'nav.group.dataSync': 'Data sync',
    'nav.dataSync':       'Sources',

    // Chat (admin)
    'chat.title':           'Live chat',
    'chat.empty':           'No conversations yet',
    'chat.unread':          'Unread',
    'chat.statusOnline':    'Online',
    'chat.statusBusy':      'Busy',
    'chat.statusOffline':   'Offline',
    'chat.placeholder':     'Type a reply (Enter to send)…',
    'chat.send':            'Send',
    'chat.image':           'Image',
    'chat.disconnected':    'Disconnected, reconnecting…',
    'chat.selectConv':      'Select a conversation from the left',
    'chat.notify':          'Sound alerts',

    'stats.title':            'Overview',
    'stats.subtitle':         'Snapshot of every account, product, and order. Last 14 days shown on charts.',
    'stats.kpi.users':        'Users (total)',
    'stats.kpi.activeProducts': 'Active products',
    'stats.kpi.orders14':     'Orders (14 d)',
    'stats.kpi.revenue14':    'Revenue (paid 14 d)',
    'stats.orders.title':     'Orders by status (lifetime)',
    'stats.orders.totalGmv':  ({ total, gmv }) => `${total} total · $${gmv} GMV across all statuses`,
    'stats.recent.title':     'Recent activity',
    'stats.recent.empty':     'No recorded activity yet.',
    'stats.event.orderPlaced': ({ id, amount }) => `Order placed · ${id} · $${amount}`,
    'stats.event.orderUpdate': ({ id, to }) => `Order ${id} → ${to ?? '?'}`,
    'stats.event.productCreate': ({ sku }) => `Product created · ${sku ?? '?'}`,
    'stats.event.productUpdate': ({ id }) => `Product updated · ${id ?? '?'}`,
    'stats.event.productDeactivate': ({ id }) => `Product deactivated · ${id ?? '?'}`,
    'stats.event.userUpdate': ({ id }) => `User updated · ${id ?? '?'}`,
    'stats.event.ordersExport': ({ rows }) => `Orders CSV exported (${rows} rows)`,
    'stats.event.usersExport': ({ rows }) => `Users CSV exported (${rows} rows)`,
    'stats.event.referralAward': ({ amount, id }) => `Referral commission · $${amount} → ${id}`,

    'status.pending':   'pending',
    'status.paid':      'paid',
    'status.fulfilled': 'fulfilled',
    'status.refunded':  'refunded',
    'status.cancelled': 'cancelled',
    'status.active':    'active',
    'user.status.active':   'Active',
    'user.status.disabled': 'Disabled',
    'user.status.pending':  'Pending',
    'status.inactive':  'inactive',
    'status.disabled':  'disabled',

    'action.search':      'Search',
    'action.cancel':      'Cancel',
    'action.save':        'Save',
    'action.edit':        'Edit',
    'action.disable':     'Disable',
    'action.enable':      'Enable',
    'action.delete':      'Delete',
    'action.refund':      'Refund',
    'action.export':      'Export CSV',
    'action.exporting':   'Exporting…',

    'products.title':           'Products',
    'products.subtitle':        ({ n }) => `${n} total · including inactive`,
    'products.action.new':      'New product',
    'products.col.sku':         'SKU',
    'products.col.name':        'Name',
    'products.col.type':        'Type',
    'products.col.price':       'Price',
    'products.col.active':      'Active',
    'products.col.actions':     'Actions',
    'products.editor.titleNew': 'New product',
    'products.editor.titleEdit':'Edit product',
    'products.field.sku':       'SKU (uppercase)',
    'products.field.type':      'Order type',
    'products.field.currency':  'Currency',
    'products.field.price':     'Price (cents)',
    'products.field.priceHint': ({ amt }) => `≈ ${amt}`,
    'products.field.nameEn':    'Name · English',
    'products.field.nameZh':    'Name · 中文',
    'products.field.active':    'Active (visible in /shop/products)',
    'products.field.image':     'Image',
    'products.field.imageHint': 'PNG / JPG / WebP / SVG · ≤ 4 MB',
    'products.field.upload':    'Upload',
    'products.field.replace':   'Replace',
    'products.field.uploading': 'Uploading…',
    'products.field.remove':    'Remove',
    'products.field.category':  'Product category',
    'products.field.featured':  'Featured (pin to "Featured" tab)',
    'products.field.coverTag':            'Cover tag (storefront card chip)',
    'products.field.coverTagPlaceholder': 'e.g. Hot / Low stock / Limited',
    'products.field.coverTagHint':        'Up to 6 chars; pick ✕ to hide the chip',
    'products.toast.created':   'Created',
    'products.toast.updated':   'Updated',
    'products.toast.disabled':  'Disabled',
    'products.toast.enabled':   'Enabled',
    'products.toast.deleted':   'Deleted',
    'products.toast.deleteFailed':'Delete failed',
    'products.toast.saveFailed':'Save failed',
    'products.toast.updateFailed':'Update failed',
    'products.field.priceUsd':  'Price · USD ($)',
    'products.field.priceCny':  'Price · CNY (¥)',
    'products.field.priceUsdOrig': 'Original USD (strikethrough)',
    'products.field.priceCnyOrig': 'Original CNY (strikethrough)',
    'products.field.priceHelp': 'At least one current price. Set the original to show "save ¥N" on the storefront.',
    'products.field.subtitleZh': 'Subtitle (Chinese)',
    'products.field.subtitleEn': 'Subtitle (English)',
    'products.field.topupColMonths': 'Months',
    'products.field.topupColCnyOrig': 'CNY orig',
    'products.field.topupColUsdOrig': 'USD orig',
    'products.field.detailSection': 'Product detail-page fields',
    'products.field.detailHint':    '(optional; powers the storefront detail page)',
    'products.field.images':        'Images',
    'products.field.imagesHint':    'Up to 5; the first is the default cover (click ★ on others to swap)',
    'products.field.cover':         'Cover',
    'products.field.makeCover':     'Make cover',
    'products.field.brand':         'Brand',
    'products.field.variant':       'Variant',
    'products.field.variantTopup':  'Top-up',
    'products.field.variantReady':  'Ready-made',
    'products.field.topupOptions':  'Top-up duration options (independent pricing)',
    'products.field.topupOptionsHint': 'Months / CNY price / USD price; blank = inherit base. Quick-adds below.',
    'products.field.topupMonthsSuffix': 'mo',
    'products.field.emailRequired': 'Email field',
    'products.field.emailNever':    'Hidden',
    'products.field.emailCart':     'Required to add to cart',
    'products.field.emailBuy':      'Required to buy',
    'products.field.tags':          'Tags',
    'products.field.addTag':        'Add tag',
    'products.field.descMdZh':      'Description (Chinese, Markdown)',
    'products.field.descMdEn':      'Description (English, Markdown)',
    'products.field.markdownInsertImage': 'Insert image',
    'products.field.policyRefs':    'Shared content templates (managed in the Policies page)',
    'products.field.policyNone':    'None',
    'products.field.autoFulfill':   'Auto-fulfill',
    'products.field.autoFulfillHint': 'System auto-fulfills on payment — no manual action needed',
    'products.field.autoFulfillNote': 'Auto-fulfill content (credentials / instructions)',
    'products.field.autoFulfillNotePlaceholder': 'e.g. Account: xxx@gmail.com\nPassword: xxxxxxxx\nSetup guide: …',
    'products.field.soldCount':     'Sold count (display only)',
    'products.field.stock':         'Stock',
    'policies.title':         'Policies',
    'policies.subtitle':      ({ n }) => `${n} total`,
    'policies.action.new':    'New template',
    'policies.filter.all':    'All types',
    'policies.type.delivery': 'Delivery',
    'policies.type.address':  'Where to use',
    'policies.type.notice':   'Heads-up',
    'policies.col.type':      'Type',
    'policies.col.slug':      'Slug',
    'policies.col.title':     'Name',
    'policies.col.actions':   'Actions',
    'policies.editor.titleNew':  'New policy template',
    'policies.editor.titleEdit': 'Edit policy template',
    'policies.field.type':       'Type (immutable after create)',
    'policies.field.slug':       'Slug',
    'policies.field.titleZh':    'Name (Chinese)',
    'policies.field.titleEn':    'Name (English)',
    'policies.delivery.key':    'Key',
    'policies.delivery.value':  'Value',
    'policies.delivery.addRow': 'Add row',
    'policies.delivery.hint':   'Footer hint (optional)',
    'policies.address.label':   'Label',
    'policies.address.value':   'Value',
    'policies.address.sub':     'Sub-text (optional)',
    'policies.address.addCell': 'Add cell',
    'policies.address.ipNote':  'IP / network note (optional)',
    'policies.address.markdown':              'Rich-text Markdown (optional · overrides cells below)',
    'policies.address.markdownPlaceholder':   'Empty = use the "cells + IP note" layout below\nFilled = render this markdown directly in the product "How to use" tab\n\nExample:\n## 📖 Heading\n### Section\n- List\n```code```\n> Blockquote',
    'policies.address.markdownActiveHint':    'Markdown is set — the cells + IP note below will be hidden on the storefront (kept here as a draft).',
    'policies.notice.itemTitle': 'Item title',
    'policies.notice.itemBody':  'Item body',
    'policies.notice.addItem':   'Add item',
    'policies.toast.created':       'Created',
    'policies.toast.updated':       'Updated',
    'policies.toast.deleted':       'Deleted',
    'policies.toast.deleteFailed':  'Delete failed',
    'policies.toast.saveFailed':    'Save failed',
    'policies.confirmDelete.title':   'Delete policy template?',
    'policies.confirmDelete.body':    ({ slug }) => `${slug} will be permanently deleted. Products referencing this template will show that section as empty.`,
    'policies.confirmDelete.confirm': 'Delete',
    'policies.confirmDelete.cancel':  'Cancel',

    // Data dictionaries
    'dict.title':              'Dictionaries',
    'dict.subtitle':           ({ n }) => `${n} entries`,
    'dict.tab.variant':        'Order type',
    'dict.tab.cat':            'Category',
    'dict.tab.brand':          'Brand',
    'dict.action.new':         'New entry',
    'dict.col.code':           'code',
    'dict.col.labelZh':        'Label (zh)',
    'dict.col.labelEn':        'Label (en)',
    'dict.col.sortOrder':      'Order',
    'dict.col.active':         'Active',
    'dict.col.actions':        'Actions',
    'dict.empty':              'No entries yet',
    'dict.editor.titleNew':    'New dictionary entry',
    'dict.editor.titleEdit':   'Edit dictionary entry',
    'dict.field.code':         'code (avoid changing after rows reference it)',
    'dict.field.code.hint':    'lowercase letters, digits, hyphens; this is the value stored in product metadata',
    'dict.field.labelZh':      'Label (Chinese)',
    'dict.field.labelEn':      'Label (English, optional)',
    'dict.field.sortOrder':    'Sort order (lower first)',
    'dict.field.active':       'Active (off hides from customer site)',
    'dict.action.save':        'Save',
    'dict.action.cancel':      'Cancel',
    'dict.toast.created':      'Created',
    'dict.toast.updated':      'Updated',
    'dict.toast.deactivated':  'Deactivated',
    'dict.toast.saveFailed':   'Save failed',
    'dict.toast.deleteFailed': 'Deactivate failed',
    'dict.confirmDelete.title':   'Deactivate this entry?',
    'dict.confirmDelete.body':    ({ code }) => `${code} will be marked inactive. Existing products using it can still resolve the label, but it won't appear in pickers or the customer site.`,
    'dict.confirmDelete.confirm': 'Deactivate',
    'dict.confirmDelete.cancel':  'Cancel',

    // Menus management
    'menus.title':           'Menus',
    'menus.subtitle':        ({ n }) => `${n} entries`,
    'menus.tab.marketing':   'Marketing site',
    'menus.tab.workspace':   'Workspace',
    'menus.tab.admin':       'Admin',
    'menus.col.code':        'code',
    'menus.col.labelZh':     'Label (zh)',
    'menus.col.labelEn':     'Label (en)',
    'menus.col.sortOrder':   'Order',
    'menus.col.visible':     'Visible',
    'menus.col.active':      'Active',
    'menus.col.actions':     'Actions',
    'menus.action.save':     'Save',
    'menus.toast.saved':     'Saved',
    'menus.toast.saveFailed':'Save failed',

    'products.confirmDelete.title':   'Delete product?',
    'products.confirmDelete.body':    ({ sku }) => `${sku} will be permanently removed. Past order line items will keep referencing the product id, but the name/image won't render anymore.`,
    'products.confirmDelete.confirm': 'Delete',
    'products.confirmDelete.cancel':  'Cancel',

    'users.title':         'Users',
    'users.subtitle':      ({ n }) => `${n} total`,
    'users.search':        'Search email, display name, referral code…',
    'users.filter.all':    'all status',
    'users.empty':         'No users match the filter.',
    'users.toggle':        ({ from, to }) => `${from} → ${to}`,
    'users.col.coins':     'Coins (¥)',
    'users.col.role':      'Role',
    'users.action.adjustWallet': 'Adjust coins',
    'users.toast.walletAdjusted':    'Wallet updated',
    'users.toast.walletAdjustFailed':'Wallet adjust failed',
    'users.walletDialog.title':   'Adjust user wallet',
    'users.walletDialog.current': 'Current balance',
    'users.walletDialog.delta':   'Delta (positive credits, negative debits)',
    'users.walletDialog.deltaHint': 'Unit: ¥. 1 coin = ¥1.',
    'users.walletDialog.note':    'Note (required)',
    'users.walletDialog.notePh':  'Referral payout / support comp / refund …',
    'users.walletDialog.after':   'After balance',
    'users.action.setPassword': 'Set password',
    'users.toast.passwordSet':       'Password updated',
    'users.toast.passwordSetFailed': 'Password update failed',
    'users.passwordDialog.title':   'Set user password',
    'users.passwordDialog.label':   'New password',
    'users.passwordDialog.hint':    '8 to 128 characters. Tell the user out-of-band.',
    'users.passwordDialog.confirm': 'Confirm password',
    'users.passwordDialog.mismatch':'Passwords do not match',
    'users.passwordDialog.tooShort':'At least 8 characters',

    'orders.title':        'Orders',
    'orders.subtitle':     ({ n }) => `${n} total`,
    'orders.search':       'Search by order id (last 8 chars)…',
    'orders.col.order':    'Order',
    'orders.col.user':     'User',
    'orders.col.amount':   'Amount',
    'orders.col.status':   'Status',
    'orders.col.provider': 'Provider',
    'orders.col.created':  'Created',
    'orders.col.actions':  'Actions',
    'orders.empty':        'No orders match the filter.',
    'orders.confirm.cancel':'Cancel order {id}?',
    'orders.confirm.refund':'Refund order {id}?',
    'orders.toast.statusChanged': ({ from, to }) => `${from} → ${to}`,
    'orders.toast.exportReady':   'CSV downloaded.',
    'orders.toast.exportFailed':  'Export failed',
    'orders.toast.updateFailed':  'Update failed',

    // Fulfillment dialog (the "paid → shipped" step)
    'orders.fulfill.cta':         'Ship',
    'orders.fulfill.title':       'Mark order as shipped',
    'orders.fulfill.body':        'For credential-bearing items (ready accounts, etc.) paste the username / password / login URL into the textarea — the buyer will see and copy this from "My orders". For top-up / service items leave it blank.',
    'orders.fulfill.placeholder': 'Username: ...\nPassword: ...\nLogin URL: ...\nNotes: ...\n(Leave blank for top-up / service items — no credentials needed.)',
    'orders.fulfill.hint':        'Visible only to the buyer. Double-check the content — you cannot edit it inline after submission.',
    'orders.fulfill.submitWith':  'Ship with credentials',
    'orders.fulfill.submitNone':  'Ship without credentials',
    'orders.fulfill.cancel':      'Cancel',
    'orders.fulfill.toastSent':   'Marked as shipped',
  },
}

export type TKey = keyof typeof dict.zh

function readInitialLang(): Lang {
  if (typeof window === 'undefined') return 'zh'
  const saved = localStorage.getItem(KEY)
  if (saved === 'zh' || saved === 'en') return saved
  return navigator.language?.toLowerCase().startsWith('zh') ? 'zh' : 'en'
}

interface I18nCtx {
  lang: Lang
  setLang: (l: Lang) => void
  t: (k: TKey, vars?: Record<string, string | number>) => string
}

const Ctx = createContext<I18nCtx | null>(null)

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => readInitialLang())

  useEffect(() => { document.documentElement.lang = lang === 'zh' ? 'zh-CN' : 'en' }, [lang])

  function setLang(next: Lang) {
    localStorage.setItem(KEY, next)
    setLangState(next)
  }

  function t(key: TKey, vars?: Record<string, string | number>): string {
    const phrase = dict[lang][key] ?? dict.en[key] ?? key
    if (typeof phrase === 'function') return phrase(vars ?? {})
    if (vars) {
      // {name} interpolation for plain-string entries.
      return Object.keys(vars).reduce(
        (s, k) => s.replaceAll(`{${k}}`, String(vars[k])),
        phrase,
      )
    }
    return phrase
  }

  return <Ctx.Provider value={{ lang, setLang, t }}>{children}</Ctx.Provider>
}

export function useI18n(): I18nCtx {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>')
  return ctx
}
